import { useCallback, useState } from 'react';
import { parseJson } from '~/helpers';
import { useLocalStorage } from '~/hooks';

interface UseSiteExpansionOptions {
  storage: ReturnType<typeof useLocalStorage>;
}

/**
 * Custom hook to manage site expansion state with localStorage persistence.
 */
export function useSiteExpansion({ storage }: UseSiteExpansionOptions) {
  const getLocalExpandedSiteIds = useCallback(() => {
    const localString = storage.get('calendar.expandedSiteIds');
    try {
      const parsed = parseJson(localString);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [storage]);

  const setLocalExpandedSiteIds = useCallback(
    (ids: string[]) => {
      storage.set('calendar.expandedSiteIds', JSON.stringify(ids));
    },
    [storage]
  );

  const [expandedSiteIds, setStateExpandedSiteIds] = useState<string[]>(
    getLocalExpandedSiteIds
  );

  const setExpandedSiteIds = useCallback(
    (idsOrUpdater: string[] | ((prev: string[]) => string[])) => {
      setStateExpandedSiteIds((prevIds) => {
        const nextIds =
          typeof idsOrUpdater === 'function'
            ? idsOrUpdater(prevIds)
            : idsOrUpdater;

        setLocalExpandedSiteIds(nextIds);
        return nextIds;
      });
    },
    [setLocalExpandedSiteIds]
  );

  const expandSite = useCallback(
    (siteId: string) => {
      setExpandedSiteIds((prevIds) =>
        prevIds.includes(siteId) ? prevIds : [...prevIds, siteId]
      );
    },
    [setExpandedSiteIds]
  );

  const collapseSite = useCallback(
    (siteId: string) => {
      setExpandedSiteIds((prevIds) => prevIds.filter((id) => id !== siteId));
    },
    [setExpandedSiteIds]
  );

  const isSiteExpanded = useCallback(
    (siteId: string) => expandedSiteIds.includes(siteId),
    [expandedSiteIds]
  );

  const toggleSiteExpansion = useCallback(
    (siteId: string) => {
      setExpandedSiteIds((prevIds) =>
        prevIds.includes(siteId)
          ? prevIds.filter((id) => id !== siteId)
          : [...prevIds, siteId]
      );
    },
    [setExpandedSiteIds]
  );

  return {
    expandedSiteIds,
    setExpandedSiteIds,
    expandSite,
    collapseSite,
    isSiteExpanded,
    toggleSiteExpansion,
  };
}
